import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input, Modal, Segmented, Select } from 'antd';

import CategorySelect from '../discover/CategorySelect';
import ToggleRow from '../common/ToggleRow';
import { useCollections } from '../../contexts/CollectionsContext';
import { canManage } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { slugify } from '../../utils/strings';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { apiErrorMessage } from '../../utils/apiErrors';
import { atDraftLimit, createPublisherPackage, draftLimitWords, packageableCards, publishingRefusal } from '../../services/publishing';
import RequirementFields, { requirementsBuildBody, requirementsRefusal } from '../packages/RequirementFields';
import { keyPrefixOf } from './publishingWords';

const KEY_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;

/**
 * The body the API takes for what the dialog holds (keepr-api KPR-1331: never
 * a publisher — the door names it). `requirements`: the cards marked Can use a
 * card you have (KPR-1816), the form's rows.
 */
export function createBody(values, prefix, requirements = []) {
	const kind = values.kind || 'collection';
	return {
		key: `${prefix}${String(values.key || '').trim()}`,
		kind,
		...(values.name ? { name: values.name.trim() } : {}),
		...(values.category ? { category: values.category } : {}),
		...(kind !== 'collection' && values.cardIds && [].concat(values.cardIds).length ? { cardIds: [].concat(values.cardIds) } : {}),
		...(values.includeItems ? { includeItems: true, ...(kind === 'collection' ? { itemsRole: values.itemsRole || 'demo' } : {}) } : { includeItems: false }),
		...(kind !== 'collection' ? requirementsBuildBody(requirements) : {}),
	};
}

/**
 * Package a collection… (keepr-api KPR-1331): a DRAFT from a collection the
 * person manages, published by their publisher and keyed under it
 * (`acme/<name>`, KPR-358). Only the choices a first version needs: what to
 * package, its key and its words on Discover, and whether its items travel
 * as examples. A draft is nobody's to install, so nothing here is reviewed;
 * its publish is.
 *
 * `drafts` is the publisher's allowance (keepr-api KPR-1445: `{ count, limit }`
 * never-listed packages, or null when unknown). At the limit the dialog says
 * so in one line and Make draft waits; the API refuses a create past it
 * anyway (409 `draft_limit_reached`, worded the same).
 */
export default function PackageCollectionModal({ open, publisher, drafts = null, onClose, onCreated }) {
	const [form] = Form.useForm();
	const { collections } = useCollections({ refreshOnOpen: open });
	const [cards, setCards] = useState([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const kind = Form.useWatch('kind', form) || 'collection';
	const collectionId = Form.useWatch('collectionId', form);
	const includeItems = Form.useWatch('includeItems', form);
	const chosenCardIds = Form.useWatch('cardIds', form);
	const prefix = keyPrefixOf(publisher);
	const full = atDraftLimit(drafts);
	// Only what the person manages: the API refuses any other (403, or 404 for one they cannot see).
	const managed = useMemo(() => (collections || []).filter(c => canManage(c) && c.status !== 'archived'), [collections]);

	useEffect(() => {
		if (!open) return;
		form.resetFields();
		setError(null);
	}, [open, form]);

	useEffect(() => {
		let live = true;
		setCards([]);
		form.setFieldsValue({ cardIds: undefined, requirements: [] });
		if (!collectionId) return undefined;
		packageableCards(collectionId).then((list) => { if (live) setCards(list); }).catch(() => { if (live) setCards([]); });
		return () => { live = false; };
	}, [collectionId, form]);

	const submit = async () => {
		const values = await form.validateFields();
		setBusy(true);
		setError(null);
		try {
			const made = await createPublisherPackage(values.collectionId, createBody(values, prefix, form.getFieldValue('requirements')));
			onCreated?.(made);
		} catch (e) {
			const body = e?.response?.data;
			setError(publishingRefusal(e) || requirementsRefusal(e) || (body?.where ? `${apiErrorMessage(e)} (${body.where})` : apiErrorMessage(e, 'The package could not be made.')));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Package a collection"
			okText="Make draft"
			onOk={submit}
			onCancel={onClose}
			okButtonProps={{ loading: busy, disabled: full }}
			destroyOnHidden
		>
			{full ? <Alert type="warning" showIcon className="publishing-modal__limit" message={draftLimitWords(drafts.limit)} /> : null}
			<Form form={form} layout="vertical" initialValues={{ kind: 'collection', itemsRole: 'demo', includeItems: false }} requiredMark={false}>
				<Form.Item
					name="collectionId"
					label="Collection"
					extra="One you manage. The package is a copy of its cards (and, if you choose, its items) as they are now."
					rules={[{ required: true, message: 'Choose a collection.' }]}
				>
					<Select
						showSearch
						optionFilterProp="label"
						placeholder="Choose a collection"
						options={managed.map(c => ({ value: normalizeId(c._id), label: c.name }))}
						onChange={(value) => {
							const name = managed.find(c => normalizeId(c._id) === value)?.name;
							if (name && !form.getFieldValue('key')) form.setFieldsValue({ key: slugify(name).slice(0, 64), name });
						}}
						aria-label="Collection"
						notFoundContent="No collection you manage"
					/>
				</Form.Item>
				<Form.Item name="kind" label="What to package">
					<Segmented options={[{ value: 'collection', label: 'Collection' }, { value: 'bundle', label: 'Card bundle' }, { value: 'card', label: 'Card' }]} />
				</Form.Item>
				{kind !== 'collection' ? (
					<Form.Item
						name="cardIds"
						label={kind === 'card' ? 'The card' : 'Cards'}
						extra="Their parent cards in the collection come along."
						rules={kind === 'card' ? [{ required: true, message: 'Choose the card.' }] : []}
					>
						<Select
							mode={kind === 'card' ? undefined : 'multiple'}
							placeholder={kind === 'card' ? 'Choose the card' : 'Every card the collection owns'}
							options={cards.map(card => ({ value: card._id, label: card.name }))}
							allowClear
							aria-label={kind === 'card' ? 'The card' : 'Cards'}
						/>
					</Form.Item>
				) : null}
				<RequirementFields form={form} collectionId={collectionId} kind={kind} chosen={chosenCardIds} />
				<Form.Item
					name="key"
					label="Key"
					extra={`Its address on Discover: /discover/${prefix}<key>. It never changes.`}
					rules={[{ required: true, message: 'A key is required.' }, { pattern: KEY_RE, message: 'Lowercase letters, digits and dashes, at least two.' }]}
				>
					<Input {...NO_TEXT_ASSIST} addonBefore={prefix || undefined} aria-label="Key" maxLength={64} />
				</Form.Item>
				<Form.Item name="name" label="Name on Discover">
					<Input aria-label="Name on Discover" maxLength={100} />
				</Form.Item>
				<Form.Item name="category" label="Category" extra="Publishing needs one of Discover's categories.">
					<CategorySelect aria-label="Category" />
				</Form.Item>
				<Form.Item name="includeItems" valuePropName="checked" noStyle>
					<ToggleRow
						first
						label={kind === 'collection' ? 'Include the items' : 'Include its cards\' items as examples'}
						description="Up to 2,000; attachments never travel. A reviewer reads every one before it is listed."
					/>
				</Form.Item>
				{kind === 'collection' && includeItems ? (
					<Form.Item name="itemsRole" label="The items are">
						<Segmented options={[{ value: 'demo', label: 'Examples (removable)' }, { value: 'core', label: 'Part of the package' }]} />
					</Form.Item>
				) : null}
			</Form>
			{error ? <Alert type="error" showIcon message={error} /> : null}
		</Modal>
	);
}
